import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Clock } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const attempts = useRef(0);
  const { refresh } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionId) {
      setStatus("missing");
      return;
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/subscription/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid" && data.subscription_active) {
          setStatus("paid");
          await refresh();
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        attempts.current += 1;
        if (attempts.current >= 8) {
          setStatus("timeout");
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        setStatus("error");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, refresh]);

  const statusHeadings = {
    checking: "Confirming your payment...",
    expired: "Session expired",
    missing: "Missing session id",
    timeout: "Still processing",
  };
  const headingText = statusHeadings[status] || "Something went wrong";

  return (
    <div className="max-w-xl mx-auto px-6 py-24 text-center">
      {status === "paid" ? (
        <>
          <CheckCircle2 className="mx-auto text-emerald-500" size={48} />
          <h1 className="font-display text-4xl mt-6">You're in.</h1>
          <p className="mt-3 text-gray-600">Subscription active. The customer ledger is now unlocked.</p>
          <div className="mt-8 flex justify-center gap-3">
            <Button className="rounded-[35px] shadow-sm hover:shadow-md bg-[#B31E1E] hover:bg-[#8F1717]" onClick={() => navigate("/dashboard/business")} data-testid="success-go-dashboard">Go to console</Button>
          </div>
        </>
      ) : (
        <>
          <Clock className="mx-auto text-gray-400" size={48} />
          <h1 className="font-display text-3xl mt-6">{headingText}</h1>
          <p className="mt-3 text-gray-600 text-sm">If your card was charged, your subscription will activate shortly.</p>
          <div className="mt-8"><Link to="/subscription"><Button variant="outline" className="rounded-[35px] shadow-sm hover:shadow-md">Back to subscription</Button></Link></div>
        </>
      )}
    </div>
  );
}
